import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { AlertTriangle, CheckCircle, Info, ShieldAlert } from 'lucide-react';
import { DownloadPdfButton } from '@/components/DownloadPdfButton';

const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

function LimitCard({ title, citation, cap, used, note, show }) {
  if (!show) return null;
  const remaining = Math.max(cap - used, 0);
  const pctUsed = cap > 0 ? Math.min((used / cap) * 100, 100) : 0;
  const nearLimit = pctUsed >= 90;
  const atLimit = pctUsed >= 100;

  return (
    <div className={`panel p-5 ${atLimit ? 'border-red-200' : nearLimit ? 'border-amber-200' : ''}`} data-testid={`limit-card-${citation.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="overline">{title}</span>
        <span className="text-[9px] font-mono text-[#9CA3AF]">{citation}</span>
      </div>

      {/* Progress bar */}
      <div className="mt-3 w-full h-3 bg-[#F0F1F3] rounded-sm overflow-hidden">
        <div
          className={`h-full rounded-sm transition-all ${atLimit ? 'bg-red-500' : nearLimit ? 'bg-amber-500' : 'bg-[#1B2A4A]'}`}
          style={{ width: `${pctUsed}%` }}
        />
      </div>

      {/* Values */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <span className="text-[10px] text-[#9CA3AF] block">YTD Used</span>
          <span className="text-sm font-mono font-semibold text-[#1B2A4A]" data-testid="limit-used">{fmtFull(used)}</span>
        </div>
        <div>
          <span className="text-[10px] text-[#9CA3AF] block">Remaining</span>
          <span className={`text-sm font-mono font-semibold ${remaining === 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="limit-remaining">{fmtFull(remaining)}</span>
        </div>
        <div>
          <span className="text-[10px] text-[#9CA3AF] block">Limit</span>
          <span className="text-sm font-mono font-semibold text-[#1B2A4A]" data-testid="limit-cap">{fmtFull(cap)}</span>
        </div>
      </div>

      {/* Percentage + status */}
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-xs font-mono font-medium ${atLimit ? 'text-red-600' : nearLimit ? 'text-amber-600' : 'text-[#6B7280]'}`} data-testid="limit-pct">
          {pctUsed.toFixed(1)}% used
        </span>
        {atLimit && (
          <span className="flex items-center gap-1 text-xs text-red-600 font-medium"><ShieldAlert size={12} />Limit reached</span>
        )}
        {nearLimit && !atLimit && (
          <span className="flex items-center gap-1 text-xs text-amber-600 font-medium"><AlertTriangle size={12} />Approaching limit</span>
        )}
        {!nearLimit && (
          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle size={12} />On track</span>
        )}
      </div>

      {note && <p className="mt-2 text-[10px] text-[#9CA3AF] leading-relaxed">{note}</p>}
    </div>
  );
}

export const ContributionLimitsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tsp/participant/contribution-limits-status');
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="space-y-4">{[0,1,2].map(i => <div key={i} className="panel p-5 animate-pulse"><div className="h-4 bg-gray-100 rounded w-48 mb-3" /><div className="h-3 bg-gray-50 rounded w-full mb-2" /><div className="h-8 bg-gray-50 rounded w-full" /></div>)}</div>;

  if (!data) return <div className="panel p-5"><p className="text-sm text-[#6B7280]">Unable to load contribution limits.</p></div>;

  const age = data.participant_age;
  const showStandardCatchUp = (age >= 50 && age <= 59) || age >= 64;
  const showEnhancedCatchUp = age >= 60 && age <= 63;

  return (
    <div className="space-y-4" data-testid="contribution-limits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>IRS Contribution Limits — {data.year}</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-[#9CA3AF]">Age: {age}</span>
          <DownloadPdfButton
            path="/exports/contribution-summary.pdf"
            filename="UniFed_ContributionSummary.pdf"
            label="Download Summary PDF"
            testId="download-contribution-btn"
          />
        </div>
      </div>

      {/* Age bracket info */}
      <div className="panel p-4 bg-blue-50 border-blue-200" data-testid="age-bracket-info">
        <div className="flex items-start gap-2">
          <Info size={14} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-700">
            {showEnhancedCatchUp ? (
              <p><span className="font-semibold">SECURE 2.0 Enhanced Catch-Up applies (Age 60-63):</span> Your catch-up limit is $11,250, replacing the standard $7,500. This is not additive — the enhanced amount is the total catch-up available.</p>
            ) : showStandardCatchUp ? (
              <p><span className="font-semibold">Standard Catch-Up applies (Age {age >= 64 ? '64+' : '50-59'}):</span> You are eligible for an additional $7,500 catch-up contribution above the standard $23,500 deferral limit.</p>
            ) : (
              <p><span className="font-semibold">Standard Limit Only (Age {age}, under 50):</span> Your elective deferral limit is $23,500. Catch-up contributions are not available until age 50.</p>
            )}
          </div>
        </div>
      </div>

      {/* Limit Cards */}
      <LimitCard
        title="Standard Elective Deferral"
        citation="§ 402(g)"
        cap={data.standard_limit}
        used={data.ytd_employee}
        note="Applies to all participants. Includes Traditional and Roth employee contributions."
        show={true}
      />

      <LimitCard
        title="Standard Catch-Up Contribution"
        citation="§ 414(v)"
        cap={data.catch_up_limit}
        used={Math.max(data.ytd_employee - data.standard_limit, 0)}
        note="Additional limit for participants age 50-59 and 64+. Contributions above the standard $23,500 count toward this limit."
        show={showStandardCatchUp}
      />

      <LimitCard
        title="SECURE 2.0 Enhanced Catch-Up (Age 60-63)"
        citation="§ 414(v)"
        cap={data.catch_up_limit}
        used={Math.max(data.ytd_employee - data.standard_limit, 0)}
        note="Replaces the standard catch-up for ages 60-63. The enhanced $11,250 limit is the total catch-up — not additive to the standard $7,500."
        show={showEnhancedCatchUp}
      />

      <LimitCard
        title="Annual Additions (All Sources)"
        citation="§ 415(c)"
        cap={data.total_additions_limit}
        used={data.ytd_total}
        note="Includes all sources: employee contributions + agency auto 1% + agency match. Maximum combined limit from all sources."
        show={true}
      />

      {/* Breakdown Table */}
      <div className="panel p-5" data-testid="contribution-breakdown">
        <span className="overline mb-3 block">YTD Contribution Breakdown</span>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between py-2 border-b border-[#F0F1F3]">
            <span className="text-[#6B7280]">Employee Contributions (Traditional + Roth)</span>
            <span className="font-mono font-medium text-[#1B2A4A]" data-testid="ytd-employee">{fmtFull(data.ytd_employee)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#F0F1F3]">
            <span className="text-[#6B7280]">Agency Automatic 1%</span>
            <span className="font-mono font-medium text-[#1B2A4A]" data-testid="ytd-agency-auto">{fmtFull(data.ytd_agency_auto)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#F0F1F3]">
            <span className="text-[#6B7280]">Agency Match</span>
            <span className="font-mono font-medium text-[#1B2A4A]" data-testid="ytd-agency-match">{fmtFull(data.ytd_agency_match)}</span>
          </div>
          <div className="flex justify-between py-2 bg-[#F8F9FA] px-3 rounded">
            <span className="font-medium text-[#1B2A4A]">Total All Sources</span>
            <span className="font-mono font-bold text-[#1B2A4A]" data-testid="ytd-total">{fmtFull(data.ytd_total)}</span>
          </div>
        </div>
      </div>

      {/* Reference */}
      <div className="text-[10px] text-[#9CA3AF] text-center leading-relaxed">
        26 U.S.C. § 402(g) Elective Deferrals · 26 U.S.C. § 414(v) Catch-Up · 26 U.S.C. § 415(c) Annual Additions · SECURE 2.0 Act Enhanced Catch-Up
      </div>
    </div>
  );
};
